import type { CharacterState } from '../character/character-state';
import type { Facing } from '../contracts/presentation-snapshot';
import { createInputFrame, EMPTY_INPUT_FRAME } from '../input/input-frame';
import type { InputFrame } from '../input/input-frame';
import type { RandomSource } from '../random/mulberry32';

export const CPU_ACTIONS = [
  'LIGHT', 'HEAVY', 'SKILL_1', 'SKILL_2', 'JUMP', 'CROUCH', 'GUARD',
  'MOVE_FORWARD', 'MOVE_BACK', 'IDLE', 'SUPER',
] as const;
export type CpuAction = (typeof CPU_ACTIONS)[number];
export type VirtualInput = InputFrame;
export const CPU_DISTANCE_BANDS = ['CLOSE', 'MID', 'FAR'] as const;
export type CpuDistanceBand = (typeof CPU_DISTANCE_BANDS)[number];
export type CpuWeights = Readonly<Record<CpuAction, number>>;

export interface CpuProfile {
  readonly id: string;
  readonly weights: Readonly<Record<CpuDistanceBand, CpuWeights>>;
  readonly superBonus: Readonly<Record<CpuDistanceBand, number>>;
  readonly counterAction?: CpuAction;
  readonly trapAction?: CpuAction;
}

export interface CpuObservation {
  readonly selfState: CharacterState;
  readonly facing: Facing;
  readonly distance: number;
  readonly legalActions: ReadonlySet<CpuAction>;
}

export interface CpuQueuedDecision {
  readonly decisionId: number;
  readonly action: CpuAction;
  remainingDelayFrames: number;
  readonly heldFrames: number;
}

export interface CpuHeldPlan {
  readonly action: CpuAction;
  remainingFrames: number;
}

export interface CpuRuntimeState {
  decisionTimer: number;
  nextDecisionId: number;
  queue: CpuQueuedDecision[];
  heldPlans: CpuHeldPlan[];
  decisions: CpuAction[];
}

export const CPU_HELD_FRAMES: Readonly<Record<CpuAction, number>> = Object.freeze({
  IDLE: 0, MOVE_FORWARD: 12, MOVE_BACK: 10, JUMP: 1, CROUCH: 15, GUARD: 18,
  LIGHT: 1, HEAVY: 1, SKILL_1: 1, SKILL_2: 1, SUPER: 1,
});

export const getCpuDistanceBand = (distance: number): CpuDistanceBand => {
  if (!Number.isFinite(distance) || distance < 0) throw new RangeError('distance must be finite and non-negative.');
  if (distance <= 110) return 'CLOSE';
  if (distance <= 240) return 'MID';
  return 'FAR';
};

const inclusiveInteger = (random: RandomSource, minimum: number, maximum: number): number =>
  minimum + Math.floor(random.next() * (maximum - minimum + 1));

const selectWeightedAction = (randomValue: number, profile: CpuProfile, observation: CpuObservation): CpuAction => {
  const band = getCpuDistanceBand(observation.distance);
  const candidates = CPU_ACTIONS.map((action) => ({
    action,
    weight: observation.legalActions.has(action)
      ? profile.weights[band][action] + (action === 'SUPER' ? profile.superBonus[band] : 0)
      : 0,
  })).filter((candidate) => candidate.weight > 0);
  const total = candidates.reduce((sum, candidate) => sum + candidate.weight, 0);
  if (total <= 0) return 'IDLE';
  let cursor = Math.floor(randomValue * total);
  for (const candidate of candidates) {
    if (cursor < candidate.weight) return candidate.action;
    cursor -= candidate.weight;
  }
  return candidates[candidates.length - 1]?.action ?? 'IDLE';
};

const actionInput = (action: CpuAction, facing: Facing): InputFrame => {
  switch (action) {
    case 'MOVE_FORWARD': return createInputFrame({ [facing === 'RIGHT' ? 'right' : 'left']: true });
    case 'MOVE_BACK': return createInputFrame({ [facing === 'RIGHT' ? 'left' : 'right']: true });
    case 'JUMP': return createInputFrame({ jump: true });
    case 'CROUCH': return createInputFrame({ down: true });
    case 'GUARD': return createInputFrame({ guard: true });
    case 'LIGHT': return createInputFrame({ light: true });
    case 'HEAVY': return createInputFrame({ heavy: true });
    case 'SKILL_1': return createInputFrame({ skill1: true });
    case 'SKILL_2': return createInputFrame({ skill2: true });
    case 'SUPER': return createInputFrame({ super: true });
    case 'IDLE': return EMPTY_INPUT_FRAME;
  }
};

const mergeInputs = (inputs: readonly InputFrame[]): InputFrame => createInputFrame({
  left: inputs.some((input) => input.left), right: inputs.some((input) => input.right),
  down: inputs.some((input) => input.down), guard: inputs.some((input) => input.guard),
  light: inputs.some((input) => input.light), heavy: inputs.some((input) => input.heavy),
  skill1: inputs.some((input) => input.skill1), skill2: inputs.some((input) => input.skill2),
  super: inputs.some((input) => input.super), jump: inputs.some((input) => input.jump),
});

export class NormalCpuController {
  private runtime: CpuRuntimeState = {
    decisionTimer: 0, nextDecisionId: 0, queue: [], heldPlans: [], decisions: [],
  };

  public constructor(public readonly profile: CpuProfile) {}

  public step(observation: CpuObservation, random: RandomSource): VirtualInput {
    const emitted: InputFrame[] = [];
    for (const entry of this.runtime.queue) {
      if (entry.remainingDelayFrames > 0) entry.remainingDelayFrames -= 1;
    }
    const retainedQueue: CpuQueuedDecision[] = [];
    let discreteEmitted = false;
    for (const entry of [...this.runtime.queue].sort((left, right) => left.decisionId - right.decisionId)) {
      if (entry.remainingDelayFrames > 0) {
        retainedQueue.push(entry);
        continue;
      }
      const discrete = ['LIGHT', 'HEAVY', 'SKILL_1', 'SKILL_2', 'SUPER', 'JUMP'].includes(entry.action);
      if (discrete) {
        if (discreteEmitted) retainedQueue.push(entry);
        else {
          emitted.push(actionInput(entry.action, observation.facing));
          discreteEmitted = true;
        }
      } else if (entry.action === 'IDLE') {
        this.runtime.heldPlans = [];
      } else {
        this.runtime.heldPlans = [{ action: entry.action, remainingFrames: entry.heldFrames }];
      }
    }
    this.runtime.queue = retainedQueue;

    const heldPlan = this.runtime.heldPlans[0];
    if (heldPlan !== undefined) {
      emitted.push(actionInput(heldPlan.action, observation.facing));
      heldPlan.remainingFrames -= 1;
      if (heldPlan.remainingFrames === 0) this.runtime.heldPlans = [];
    }

    if (this.runtime.decisionTimer > 0) this.runtime.decisionTimer -= 1;
    if (this.runtime.decisionTimer === 0) {
      const interval = inclusiveInteger(random, 6, 10);
      const delay = inclusiveInteger(random, 12, 18);
      const action = selectWeightedAction(random.next(), this.profile, observation);
      this.runtime.queue.push({
        decisionId: this.runtime.nextDecisionId,
        action,
        remainingDelayFrames: delay,
        heldFrames: CPU_HELD_FRAMES[action],
      });
      this.runtime.nextDecisionId += 1;
      this.runtime.decisionTimer = interval;
      this.runtime.decisions.push(action);
    }
    return mergeInputs(emitted);
  }

  public getRuntimeClone(): CpuRuntimeState {
    return {
      decisionTimer: this.runtime.decisionTimer,
      nextDecisionId: this.runtime.nextDecisionId,
      queue: this.runtime.queue.map((entry) => ({ ...entry })),
      heldPlans: this.runtime.heldPlans.map((plan) => ({ ...plan })),
      decisions: [...this.runtime.decisions],
    };
  }

  public restore(runtime: Readonly<CpuRuntimeState>): void {
    this.runtime = {
      decisionTimer: runtime.decisionTimer,
      nextDecisionId: runtime.nextDecisionId,
      queue: runtime.queue.map((entry) => ({ ...entry })),
      heldPlans: runtime.heldPlans.map((plan) => ({ ...plan })),
      decisions: [...runtime.decisions],
    };
  }

  public reset(): void {
    this.runtime = { decisionTimer: 0, nextDecisionId: 0, queue: [], heldPlans: [], decisions: [] };
  }

  public flush(): void {
    this.runtime.queue = [];
    this.runtime.heldPlans = [];
  }

  public clone(): NormalCpuController {
    const clone = new NormalCpuController(this.profile);
    clone.restore(this.runtime);
    return clone;
  }
}
